<template>
  <div class="menu__weeks my-4 pa-2">
    <template v-if="weeks.loading">
      <v-skeleton-loader type="card" min-height="250px" />
    </template>
    <template v-else>
      <v-sheet class="mx-auto" max-width="700">
        <v-slide-group v-model="selectedYear" mandatory>
          <v-slide-item
            v-for="year in availableYears"
            v-slot="{ active, toggle }"
            :key="year"
            :value="year"
          >
            <v-btn
              class="mx-2"
              :input-value="active"
              active-class="purple white--text"
              depressed
              :disabled="year < 2025"
              rounded
              @click="toggle"
            >
              {{ year }}
            </v-btn>
          </v-slide-item>
        </v-slide-group>
      </v-sheet>
      <v-window v-model="currentSlide" show-arrows>
        <v-window-item
          v-for="(groups, gWeeksIndex) in groupedWeeks"
          :key="`slide-${groups[0]?.weekYear || gWeeksIndex}`"
          :value="gWeeksIndex"
          :class="{ 'disabled-slide': weeks.loading }"
        >
          <div class="m-5">
            <div class="row">
              <div
                v-for="week in groups"
                :key="week.weekYear"
                class="col-3 slider__bloq"
                :class="{ 'is-disabled': isDisabled || !week.isSelectable }"
              >
                <div
                  class="week-bloq"
                  :class="{ active: activeWeek === week.weekYear }"
                  @click="onActiveWeekChanged(week)"
                >
                  {{ week.interval }}
                  <p class="mb-0 text-body-2">
                    Ppto Semana {{ week.week }} {{ week.year }}
                  </p>
                  <h6 class="font-weight-bold">
                    {{ formatNumber(week.weeklyBudget, '$', 0) }}
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </v-window-item>
      </v-window>

      <v-btn
        icon
        class="calendar__floating-action"
        @click.prevent="onResetSelectedYear"
      >
        <v-icon size="30" color="green">mdi-calendar-check-outline</v-icon>
      </v-btn>
    </template>
  </div>
</template>

<script>
  import axios from '@/plugins/axios';
  import moment from 'moment';
  import numbersFormats from '@/mixins/numbersFormats';
  import DateFormats from '@/core/constants/DateFormats';

  export default {
    name: 'WeekSlider',
    mixins: [numbersFormats],
    props: {
      weeksPerSlide: {
        type: Number,
        default: 4,
      },
      disableSendWeek: {
        type: Boolean,
      },
      username: {
        type: String,
        required: true,
      },
    },

    data() {
      return {
        currentSlide: 0,
        activeWeek: null,
        selectedYear: new Date().getFullYear(),
        weeks: {
          data: [],
          loading: false,
        },
      };
    },
    computed: {
      availableYears() {
        const numbersOfYears = 5;
        const currentYear = moment().subtract(numbersOfYears, 'year').year();
        const years = [];

        for (let i = 1; i <= numbersOfYears; i++) {
          years.push(currentYear + i);
        }

        return years;
      },

      sortedWeeks() {
        return [...this.weeks.data].sort(
          (a, b) => Number(a.weekYear) - Number(b.weekYear)
        );
      },
      groupedWeeks() {
        const result = [];
        const weeks = this.sortedWeeks;
        for (let i = 0; i < weeks.length; i += this.weeksPerSlide) {
          result.push(weeks.slice(i, i + this.weeksPerSlide));
        }
        return result;
      },
      isDisabled() {
        return this.disableSendWeek;
      },
    },
    watch: {
      selectedYear: {
        immediate: true,
        handler() {
          this.onSelectedYearChanged();
        },
      },
    },
    methods: {
      async getWeeks(year) {
        try {
          this.weeks.loading = true;
          this.weeks.data = [];
          const res = await axios.get('/earnings/weeks', {
            params: {
              year,
              user: this.username,
            },
            noCancelRequest: true,
          });
          this.weeks.data = this.normalizeWeeks(res.data.data);
        } catch (error) {
          this.$toast.warning('No se pudieron cargar las semanas disponibles');
        }
        this.weeks.loading = false;
      },
      normalizeWeeks(data) {
        if (!Array.isArray(data)) return [];
        const today = moment();
        return data.flatMap((month) =>
          (month.weeks || []).map((week) => {
            const isSelectable = moment(week.range.startDate).isSameOrBefore(
              today,
              'day'
            );

            return {
              ...week,
              totalWeeks: month.totalWeeks,
              interval: this.formatInterval(week),
              isSelectable,
            };
          })
        );
      },
      async onSelectedYearChanged() {
        await this.getWeeks(this.selectedYear);
        if (!this.weeks.data?.length) return;
        const currentYear = new Date().getFullYear();
        const resolver =
          this.selectedYear === currentYear
            ? this.getCurrentWeek()
            : this.getLastWeekOfSelectedYear();
        if (!resolver) return;
        const { slideIndex, data } = resolver;
        this.currentSlide = slideIndex;
        this.activeWeek = data.weekYear;
        this.emitWeek(data);
      },
      getLastWeekOfSelectedYear() {
        const weeks = this.sortedWeeks;
        if (!weeks.length) return;
        const lastIndex = weeks.length - 1;
        const lastWeek = weeks[lastIndex];
        return {
          slideIndex: Math.floor(lastIndex / this.weeksPerSlide),
          data: lastWeek,
        };
      },
      getCurrentWeek() {
        const today = moment().format(DateFormats.ISO_DATE);

        for (const [slideIndex, group] of this.groupedWeeks.entries()) {
          for (const week of group) {
            if (today >= week.range.startDate && today <= week.range.endDate) {
              return {
                slideIndex,
                data: week,
              };
            }
          }
        }
      },
      onActiveWeekChanged(week) {
        if (this.isDisabled || !week.isSelectable) return;

        this.activeWeek = week.weekYear;
        this.emitWeek(week);
      },

      onResetSelectedYear() {
        const currentYear = new Date().getFullYear();
        if (currentYear === this.selectedYear) {
          // watch  hook will not detect changes if year is the same, execute the function manually
          this.onSelectedYearChanged();
        } else {
          this.selectedYear = currentYear;
        }
      },
      emitWeek(week) {
        this.$emit('update:week', week);
      },
      formatInterval(week) {
        const start = moment(week.range.startDate).format(
          DateFormats.DAY_MONTH
        );
        const end = moment(week.range.endDate).format(DateFormats.DAY_MONTH);

        return `${start} - ${end}`;
      },
    },
  };
</script>

<style scoped>
  .disabled-slide {
    pointer-events: none;
    opacity: 0.5;
  }
  .active {
    color: #10628b;
  }
  .week-bloq {
    cursor: pointer;
    text-align: center;
  }
  .slider__bloq {
    border-right: 1px solid #c1c1c1;
    padding: 5px 0 5px 0;
  }
  .slider__bloq:last-child {
    border-right: 0;
  }
  .menu__weeks {
    position: relative;
  }
  .calendar__floating-action {
    position: absolute;
    right: 0;
    top: 45px;
  }
  .is-disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }
</style>
